import type { ErrorInfo } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { formatInTimeZone } from "date-fns-tz";

import { store } from "app/store";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";

export const getErrorDetails = (
  error: Error,
  errorInfo: ErrorInfo,
  triggeredBy: string
) => {
  let sessionId = "";
  let applicationId = "";

  try {
    const context = datadogRum.getInternalContext();
    sessionId = context?.session_id ?? "";
    applicationId = context?.application_id ?? "";
  } catch (error) {
    console.error(error);
  }

  let errorDetails = sessionId;

  try {
    const state = store.getState();
    const user = selectCurrentUser(state);
    const practice = selectCurrentPractice(state);
    const clinic = selectCurrentClinic(state);

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Get user agent
    const userAgent = navigator.userAgent;

    // Get connection speed info if available
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;
    const connectionInfo = connection
      ? {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt
        }
      : null;

    // Get page load time using modern Performance API
    const navigationEntry = window.performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming | undefined;
    // Only calculate if load event has completed (loadEventEnd > 0)
    const pageLoadTime =
      navigationEntry && navigationEntry.loadEventEnd > 0
        ? navigationEntry.loadEventEnd - navigationEntry.fetchStart
        : null;

    // Memory usage (if available)
    const memory = (window.performance as any).memory;

    errorDetails = `Triggered By: ${triggeredBy}
DateTime (America/New_York): ${formatInTimeZone(new Date(), "America/New_York", "yyyy-MM-dd HH:mm:ssXXX")}
Timestamp: ${new Date().toISOString()}
Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}

Session ID: ${sessionId}
Application ID: ${applicationId}
Version: ${__APP_VERSION__}

Practice: ${practice?.id || "N/A"} ${practice?.name || "N/A"}
Clinic: ${clinic?.id || "N/A"} ${clinic?.name || "N/A"}
User: ${user?.id || "N/A"} ${user?.first_name || "N/A"} ${user?.last_name || "N/A"}

URL: ${window.location.href}
Page Load Time: ${pageLoadTime ? `${pageLoadTime.toFixed(2)} ms` : "N/A"}
Memory Usage: ${memory ? `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB` : "N/A"}

User Agent: ${userAgent}
Service Worker: ${"serviceWorker" in navigator}

Viewport: ${viewportWidth}x${viewportHeight}
Screen Resolution: ${window.screen.width}x${window.screen.height}
Color Depth: ${window.screen.colorDepth}
Pixel Ratio: ${window.devicePixelRatio}

${
  connectionInfo
    ? `Connection Type: ${connectionInfo.effectiveType}
Downlink: ${connectionInfo.downlink} Mbps
RTT: ${connectionInfo.rtt} ms`
    : "Connection Info: Not available"
}

Error: ${error?.message}
Component Stack: ${errorInfo.componentStack}
    `;
  } catch (error) {
    console.error(error);
  }

  return errorDetails;
};
